import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Card, Text, Box, Heading, Flex } from '@radix-ui/themes';
import { IconSvg } from './IconSvg';
import { CopyButton } from './common/CopyButton';
import { WordInfo } from '../types';
import { conjugate, adjConjugate, conjugateAuxiliaries, Auxiliary } from 'kamiya-codec';
import './WordCard.css';

interface WordCardProps {
  wordInfo: WordInfo;
  onClose?: () => void;
  layout?: 'scroll' | 'carousel';
}

interface ConjugationInfo {
  form: string;
  value: string[];
  description: string;
}

// 变形类型分组
const conjugationGroups = [
  {
    title: '基本变形',
    forms: [
      { type: 'Dictionary', name: '辞书形', description: '动词的基本形式，用于查词典' },
      { type: 'Negative', name: '否定形', description: '表示否定的形式（～ない）' },
      { type: 'Conjunctive', name: '连用形', description: '用于连接其他词或语法形式' },
      { type: 'Te', name: 'て形', description: '用于请求、连接、进行时等多种用途' },
      { type: 'Ta', name: 'た形', description: '表示过去时态或完成态' },
      { type: 'Conditional', name: '条件形', description: '表示条件"如果～"（～ば）' },
      { type: 'Imperative', name: '命令形', description: '表示命令' },
      { type: 'Volitional', name: '意志形', description: '表示说话人的意志"让我们～"' },
      { type: 'Zu', name: 'ず形', description: '文语否定形式' },
      { type: 'Tara', name: 'たら形', description: '表示假设条件"如果～"' },
      { type: 'Tari', name: 'たり形', description: '表示举例"～等等"' }
    ]
  },
  {
    title: '敬语变形',
    forms: [
      { type: 'Masu', name: '敬体形', description: '礼貌语气的形式' }
    ]
  },
  {
    title: '可能・愿望',
    forms: [
      { type: 'Potential', name: '可能形', description: '表示"能够～"' },
      { type: 'Tai', name: '愿望形', description: '表示"想要～"' },
      { type: 'Tagaru', name: '他人愿望形', description: '表示"他人想要～"' },
      { type: 'Hoshii', name: '希望形', description: '表示"希望～"' }
    ]
  },
  {
    title: '推测・传闻',
    forms: [
      { type: 'Rashii', name: '推测形', description: '表示"好像～"' },
      { type: 'SoudaHearsay', name: '传闻形', description: '表示"听说～"' },
      { type: 'SoudaConjecture', name: '样态形', description: '表示"看起来～"' }
    ]
  },
  {
    title: '语态',
    forms: [
      { type: 'SeruSaseru', name: '使役形', description: '表示"让/使～"' },
      { type: 'ReruRareru', name: '被动形', description: '表示被动语态' }
    ]
  },
  {
    title: '体貌',
    forms: [
      { type: 'TeIru', name: '进行形', description: '表示动作正在进行' },
      { type: 'TeAru', name: '结果状态形', description: '表示动作的结果状态' },
      { type: 'Shimau', name: '完了形', description: '表示动作完成' }
    ]
  }
];

// 获取变形信息
const getConjugations = (word: WordInfo): ConjugationInfo[] | null => {
  if (!word.basic_form) {
    return null;
  }

  try {
    // 如果是动词
    if (word.pos === '動詞') {
      // 判断是否为一段动词
      const typeII = word.basic_form.endsWith('る') &&
        (word.pos_detail_1 === '一段' || word.conjugated_type === '一段');

      // 获取所有变形
      const allConjugations: ConjugationInfo[] = [];

      // 基本变形
      conjugationGroups.forEach(group => {
        group.forms.forEach(form => {
          if (!word.basic_form) return; // 添加安全检查
          let value: string[] = [];
          if (form.type === 'Masu' || form.type === 'Potential' || form.type === 'Tai' ||
            form.type === 'Tagaru' || form.type === 'Hoshii' || form.type === 'Rashii' ||
            form.type === 'SoudaHearsay' || form.type === 'SoudaConjecture' ||
            form.type === 'SeruSaseru' || form.type === 'ReruRareru' ||
            form.type === 'TeIru' || form.type === 'TeAru' || form.type === 'Shimau') {
            value = conjugateAuxiliaries(word.basic_form, [form.type as Auxiliary], 'Dictionary', typeII);
          } else {
            value = conjugate(word.basic_form, form.type as any, typeII);
          }

          if (value && value.length > 0) {
            allConjugations.push({
              form: form.name,
              value: value,
              description: form.description
            });
          }
        });
      });

      return allConjugations;
    }
    // 如果是形容词
    else if (word.pos === '形容詞') {
      const isIAdjective = word.basic_form.endsWith('い');
      const conjugations: ConjugationInfo[] = [
        { form: '否定形', value: adjConjugate(word.basic_form, 'Negative', isIAdjective), description: '表示否定' },
        { form: '条件形', value: adjConjugate(word.basic_form, 'Conditional', isIAdjective), description: '表示条件"如果~"' },
        { form: 'たり形', value: adjConjugate(word.basic_form, 'Tari', isIAdjective), description: '表示举例"~等等"' },
        { form: '现在形', value: adjConjugate(word.basic_form, 'Present', isIAdjective), description: '表示现在时' },
        { form: '连体形', value: adjConjugate(word.basic_form, 'Prenomial', isIAdjective), description: '修饰名词时的形式' },
        { form: '过去形', value: adjConjugate(word.basic_form, 'Past', isIAdjective), description: '表示过去时' },
        { form: '否定过去形', value: adjConjugate(word.basic_form, 'NegativePast', isIAdjective), description: '表示过去时的否定' },
        { form: 'て形', value: adjConjugate(word.basic_form, 'ConjunctiveTe', isIAdjective), description: '用于连接' },
        { form: '副词形', value: adjConjugate(word.basic_form, 'Adverbial', isIAdjective), description: '作为副词使用时的形式' },
        { form: 'たら形', value: adjConjugate(word.basic_form, 'TaraConditional', isIAdjective), description: '表示条件"如果~"' },
        { form: '名词形', value: adjConjugate(word.basic_form, 'Noun', isIAdjective), description: '作为名词使用时的形式' },
        { form: 'そう形', value: adjConjugate(word.basic_form, 'StemSou', isIAdjective), description: '表示"看起来~"' },
        { form: '否定そう形', value: adjConjugate(word.basic_form, 'StemNegativeSou', isIAdjective), description: '表示"看起来不~"' }
      ].filter(conj => conj.value && conj.value.length > 0);

      return conjugations;
    }
    return null;
  } catch (error) {
    console.error('获取变形信息时出错:', error);
    return null;
  }
};

const InfoRow: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box className="info-row">
      <Text className="info-label">{label}</Text>
      <Text className="info-value">{value}</Text>
    </Box>
  );
};

export const WordCard: React.FC<WordCardProps> = ({ wordInfo, onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  if (!wordInfo) {
    return null;
  }

  // 获取变形信息
  let conjugations: ConjugationInfo[] | null = null;

  try {
    conjugations = getConjugations(wordInfo);
  } catch (error) {
    console.error('处理变形信息时出错:', error);
  }

  // 添加 ESC 键关闭功能
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Handle scroll to update active dot
  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollLeft = scrollRef.current.scrollLeft;
      const width = scrollRef.current.clientWidth;
      const index = Math.round(scrollLeft / width);
      setActiveSlide(index);
    }
  };

  const scrollToSlide = (index: number) => {
    if (scrollRef.current) {
      const width = scrollRef.current.clientWidth;
      scrollRef.current.scrollTo({
        left: width * index,
        behavior: 'smooth'
      });
    }
  };

  const handlePrev = () => {
    if (activeSlide > 0) {
      scrollToSlide(activeSlide - 1);
    }
  };

  const handleNext = () => {
    if (activeSlide < slides.length - 1) {
      scrollToSlide(activeSlide + 1);
    }
  };

  // 准备 Slides
  const slides = useMemo(() => {
    const list = [];

    // Slide 1: 基本信息 + 释义
    list.push({
      id: 'basic',
      title: '基本',
      content: (
        <div className="slide-content">
          <Box className="info-grid">
            <InfoRow label="原形" value={wordInfo.text} />
            <InfoRow label="読み" value={wordInfo.reading} />
            <InfoRow label="発音" value={wordInfo.pronunciation} />
            <InfoRow label="ローマ字" value={wordInfo.romaji} />
            <InfoRow label="品詞" value={wordInfo.pos} />
            <InfoRow label="品詞詳細1" value={wordInfo.pos_detail_1} />
            <InfoRow label="品詞詳細2" value={wordInfo.pos_detail_2} />
            <InfoRow label="基本形" value={wordInfo.basic_form} />
            <InfoRow label="活用型" value={wordInfo.conjugated_type} />
            <InfoRow label="活用形" value={wordInfo.conjugated_form} />
          </Box>

          {wordInfo.meanings && wordInfo.meanings.length > 0 && (
            <Box mt="4">
              <Heading as="h3" size="2" mb="3" className="section-title">MEANINGS</Heading>
              <div className="meanings-list">
                {wordInfo.meanings.map((meaning, index) => (
                  <div key={index} className="meaning-item">
                    <Text>{meaning}</Text>
                  </div>
                ))}
              </div>
            </Box>
          )}
        </div>
      )
    });

    // Slide 2: 例句
    if (wordInfo.examples && wordInfo.examples.length > 0) {
      list.push({
        id: 'examples',
        title: '例句',
        content: (
          <div className="slide-content">
            <Heading as="h3" size="2" mb="3" className="section-title">EXAMPLES</Heading>
            <div className="examples-list">
              {wordInfo.examples.map((example, index) => (
                <div key={index} className="example-quote">
                  <Text>{example}</Text>
                </div>
              ))}
            </div>
          </div>
        )
      });
    }

    // Slide 3: 变形
    if (conjugations && conjugations.length > 0) {
      list.push({
        id: 'conjugations',
        title: '变形',
        content: (
          <div className="slide-content">
            <Heading as="h3" size="2" mb="3" className="section-title">CONJUGATIONS</Heading>
            <div className="conjugations-grid">
              {conjugations.map((conj, index) => (
                <div key={index} className="conjugation-cell">
                  <Text className="conjugation-form">{conj.form}</Text>
                  <Text className="conjugation-value">{conj.value[0]}</Text>
                </div>
              ))}
            </div>
          </div>
        )
      });
    }

    // Slide 4: 汉字
    if (wordInfo.isKanjiWord && wordInfo.kanjis && wordInfo.kanjis.length > 0) {
      list.push({
        id: 'kanji',
        title: '汉字',
        content: (
          <div className="slide-content">
            <Heading as="h3" size="2" mb="3" className="section-title">KANJI</Heading>
            <div className="kanji-grid">
              {wordInfo.kanjis.map((kanji, idx) => (
                <div key={idx} className="kanji-cell">
                  <div className="kanji-char">{kanji.kanji}</div>
                  <div className="kanji-reading">{kanji.reading}</div>
                </div>
              ))}
            </div>
          </div>
        )
      });
    }

    return list;
  }, [wordInfo, conjugations]);

  return (
    <Card className="details-card premium-glass">
      {onClose && (
        <button className="close-button" onClick={onClose} aria-label="关闭">
          <IconSvg name="mdi:close" width={16} height={16} />
        </button>
      )}

      {/* 标题区域 */}
      <Box className="details-header">
        <Flex align="center" gap="2">
          <Text className="details-title">{wordInfo.text}</Text>
          <CopyButton text={wordInfo.text} size={18} className="word-copy-button" />
        </Flex>
        {wordInfo.romaji && (
          <Text className="details-subtitle">{wordInfo.romaji}</Text>
        )}
      </Box>

      {/* Tabs Navigation */}
      <div className="tabs-container">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            className={`tab-button ${index === activeSlide ? 'active' : ''}`}
            onClick={() => scrollToSlide(index)}
          >
            {slide.title}
          </button>
        ))}
      </div>

      {/* Content Area with Arrows */}
      <div className="deck-wrapper">
        {/* Left Arrow */}
        <button
          className={`nav-arrow left ${activeSlide === 0 ? 'disabled' : ''}`}
          onClick={handlePrev}
          disabled={activeSlide === 0}
        >
          <IconSvg name="mdi:chevron-left" width={24} height={24} />
        </button>

        {/* Snap Scroll Deck */}
        <div
          className="deck-container"
          ref={scrollRef}
          onScroll={handleScroll}
        >
          {slides.map((slide) => (
            <div key={slide.id} className="deck-slide">
              {slide.content}
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          className={`nav-arrow right ${activeSlide === slides.length - 1 ? 'disabled' : ''}`}
          onClick={handleNext}
          disabled={activeSlide === slides.length - 1}
        >
          <IconSvg name="mdi:chevron-right" width={24} height={24} />
        </button>
      </div>
    </Card>
  );
};